/**
 * AdminIdeaTemplates.js
 * Admin panel to manage idea generation templates and niches.
 * - List all niches with template counts
 * - Edit keywords and templates per niche inline
 * - Add new niches
 * - Control AI auto-refresh
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, Chip, IconButton, TextField,
    Accordion, AccordionSummary, AccordionDetails, Divider,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Alert, CircularProgress, Switch, FormControlLabel, Tooltip,
    alpha, useTheme,
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    AutoAwesome as AutoAwesomeIcon,
    Refresh as RefreshIcon,
    Check as CheckIcon,
    Edit as EditIcon,
} from '@mui/icons-material';
import { adminApi } from '../services/api';

// ---------------------------------------------------------------------------
// KeywordEditor — editable chip array
// ---------------------------------------------------------------------------
function KeywordEditor({ keywords, onChange }) {
    const [newKw, setNewKw] = useState('');
    const handleAdd = () => {
        const kw = newKw.trim().toLowerCase();
        if (kw && !keywords.includes(kw)) {
            onChange([...keywords, kw]);
        }
        setNewKw('');
    };
    const handleDelete = (kw) => onChange(keywords.filter(k => k !== kw));
    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
                {keywords.map(kw => (
                    <Chip
                        key={kw}
                        label={kw}
                        size="small"
                        onDelete={() => handleDelete(kw)}
                        sx={{ fontSize: '0.72rem' }}
                    />
                ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                    size="small"
                    placeholder="Add keyword…"
                    value={newKw}
                    onChange={e => setNewKw(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAdd()}
                    sx={{ flex: 1 }}
                />
                <Button size="small" variant="outlined" onClick={handleAdd} disabled={!newKw.trim()}>
                    Add
                </Button>
            </Box>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// TemplateCard — expandable, editable template
// ---------------------------------------------------------------------------
function TemplateCard({ template, index, onUpdate, onDelete }) {
    const [expanded, setExpanded] = useState(false);
    const [draft, setDraft] = useState({ ...template });

    const handleSave = () => {
        onUpdate(index, draft);
        setExpanded(false);
    };

    const sourceColor = draft.source === 'ai_generated' ? '#8b5cf6' : '#6366f1';

    return (
        <Paper variant="outlined" sx={{ mb: 1, borderRadius: '8px', overflow: 'hidden' }}>
            <Box sx={{
                px: 2, py: 1.25,
                display: 'flex', alignItems: 'center', gap: 1.5,
                cursor: 'pointer',
            }} onClick={() => setExpanded(!expanded)}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {draft.title || '(untitled)'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                        <Chip label={draft.format || '—'} size="small" sx={{ height: 18, fontSize: '0.6rem' }} />
                        {draft.source && (
                            <Chip
                                label={draft.source === 'ai_generated' ? 'AI' : 'Manual'}
                                size="small"
                                sx={{ height: 18, fontSize: '0.6rem', bgcolor: alpha(sourceColor, 0.1), color: sourceColor }}
                            />
                        )}
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                    <Tooltip title="Edit">
                        <IconButton size="small" onClick={e => { e.stopPropagation(); setExpanded(!expanded); }}>
                            <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete template">
                        <IconButton size="small" onClick={e => { e.stopPropagation(); onDelete(index); }}
                            sx={{ color: 'error.main' }}>
                            <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {expanded && (
                <Box sx={{ px: 2, pb: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    {[
                        { field: 'title', label: 'Title' },
                        { field: 'hook', label: 'Hook' },
                        { field: 'body', label: 'Body', multiline: true, rows: 4 },
                        { field: 'cta', label: 'Call to Action' },
                    ].map(({ field, label, multiline, rows }) => (
                        <TextField
                            key={field}
                            label={label}
                            fullWidth
                            size="small"
                            multiline={multiline}
                            rows={rows}
                            value={draft[field] || ''}
                            onChange={e => setDraft(d => ({ ...d, [field]: e.target.value }))}
                            sx={{ mt: 1.5 }}
                        />
                    ))}
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        <Button size="small" variant="contained" onClick={handleSave} startIcon={<CheckIcon />}>
                            Save
                        </Button>
                        <Button size="small" onClick={() => { setDraft({ ...template }); setExpanded(false); }}>
                            Cancel
                        </Button>
                    </Box>
                </Box>
            )}
        </Paper>
    );
}

// ---------------------------------------------------------------------------
// NicheEditor — keywords + templates for one niche
// ---------------------------------------------------------------------------
function NicheEditor({ niche, onSaved, onDelete }) {
    const [nicheData, setNicheData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const result = await adminApi.getIdeaNiche(niche);
            setNicheData(result.data || { keywords: [], templates: [] });
        } catch {
            setError('Failed to load niche data');
        } finally {
            setLoading(false);
        }
    }, [niche]);

    useEffect(() => { load(); }, [load]);

    const handleSave = async () => {
        setSaving(true);
        try {
            await adminApi.updateIdeaNiche(niche, nicheData);
            if (onSaved) onSaved();
        } catch {
            setError('Failed to save');
        } finally {
            setSaving(false);
        }
    };

    const updateTemplate = (idx, updated) => {
        setNicheData(d => {
            const templates = [...(d.templates || [])];
            templates[idx] = updated;
            return { ...d, templates };
        });
    };

    const deleteTemplate = (idx) => {
        setNicheData(d => ({
            ...d,
            templates: (d.templates || []).filter((_, i) => i !== idx),
        }));
    };

    const addTemplate = () => {
        setNicheData(d => ({
            ...d,
            templates: [...(d.templates || []), {
                title: 'New Template',
                hook: '',
                body: '',
                cta: '',
                platforms: ['Instagram', 'LinkedIn'],
                format: 'Carousel',
                engagement: 'High',
                tags: [],
                source: 'manual',
            }],
        }));
    };

    if (loading) return <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>;
    if (error) return <Alert severity="error" sx={{ m: 1 }}>{error}</Alert>;
    if (!nicheData) return null;

    return (
        <Box sx={{ p: 2 }}>
            {/* Keywords */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Keywords</Typography>
            <KeywordEditor
                keywords={nicheData.keywords || []}
                onChange={kws => setNicheData(d => ({ ...d, keywords: kws }))}
            />

            <Divider sx={{ my: 2 }} />

            {/* Templates */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
                    Templates ({(nicheData.templates || []).length})
                </Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={addTemplate}>
                    Add Template
                </Button>
            </Box>
            {(nicheData.templates || []).map((tmpl, i) => (
                <TemplateCard
                    key={i}
                    template={tmpl}
                    index={i}
                    onUpdate={updateTemplate}
                    onDelete={deleteTemplate}
                />
            ))}

            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <Button variant="contained" size="small" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving…' : 'Save Niche'}
                </Button>
                <Button size="small" color="error" onClick={() => onDelete(niche)} startIcon={<DeleteIcon />}>
                    Delete Niche
                </Button>
            </Box>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// AddNicheDialog
// ---------------------------------------------------------------------------
function AddNicheDialog({ open, onClose, onAdded }) {
    const [name, setName] = useState('');
    const [keywordsStr, setKeywordsStr] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleAdd = async () => {
        const niche = name.trim().toLowerCase().replace(/\s+/g, '_');
        if (!niche) { setError('Niche name required'); return; }
        const keywords = keywordsStr.split(',').map(k => k.trim().toLowerCase()).filter(Boolean);
        setLoading(true);
        try {
            await adminApi.addIdeaNiche(niche, { keywords, templates: [] });
            onAdded();
            onClose();
            setName('');
            setKeywordsStr('');
        } catch (e) {
            setError(e?.message || 'Failed to add niche');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Add New Niche</DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <TextField
                    label="Niche Name"
                    fullWidth size="small" sx={{ mt: 1, mb: 2 }}
                    value={name} onChange={e => setName(e.target.value)}
                    placeholder="e.g. photography, gaming"
                />
                <TextField
                    label="Keywords (comma-separated)"
                    fullWidth size="small" multiline rows={2}
                    value={keywordsStr} onChange={e => setKeywordsStr(e.target.value)}
                    placeholder="photo, camera, lens, portrait, lightroom"
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleAdd} disabled={loading}>
                    {loading ? 'Adding…' : 'Add Niche'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function AdminIdeaTemplates() {
    const theme = useTheme();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [refreshing, setRefreshing] = useState(false);
    const [aiEnabled, setAiEnabled] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');

    const cardStyle = {
        p: 3, borderRadius: '12px',
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
    };

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const result = await adminApi.listIdeaNiches();
            setData(result);
            setAiEnabled(result.ai_refresh_enabled || false);
        } catch {
            setError('Failed to load idea templates');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleToggleAiRefresh = async (enabled) => {
        setAiEnabled(enabled);
        try {
            await adminApi.updateIdeaTemplateMeta({ ai_refresh_enabled: enabled });
        } catch {
            setAiEnabled(!enabled); // revert
        }
    };

    const handleManualRefresh = async () => {
        setRefreshing(true);
        try {
            await adminApi.triggerIdeaRefresh();
            setSuccessMsg('AI refresh started in background');
        } catch (e) {
            setError(e?.message || 'Refresh failed');
        } finally {
            setRefreshing(false);
        }
    };

    const handleDeleteNiche = async (niche) => {
        if (!window.confirm(`Delete niche "${niche}"? This cannot be undone.`)) return;
        try {
            await adminApi.deleteIdeaNiche(niche);
            load();
        } catch {
            setError('Failed to delete niche');
        }
    };

    if (loading) return (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
            <CircularProgress />
        </Box>
    );

    return (
        <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: '-0.02em', mb: 1 }}>
                    Idea Template Manager
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Manage niche keywords and content templates for the AI idea engine.
                </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}
            {successMsg && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMsg('')}>{successMsg}</Alert>}

            {/* AI Refresh Control */}
            <Paper elevation={0} sx={{ ...cardStyle, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <AutoAwesomeIcon sx={{ color: '#8b5cf6', fontSize: 28 }} />
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Weekly AI Template Refresh
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Every Monday at 06:00 UTC, AI generates 3 fresh templates per niche.
                            {data?.last_ai_refresh
                                ? ` Last refresh: ${new Date(data.last_ai_refresh * 1000).toLocaleString()}`
                                : ' Never refreshed yet.'}
                        </Typography>
                    </Box>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={aiEnabled}
                                onChange={e => handleToggleAiRefresh(e.target.checked)}
                                color="primary"
                            />
                        }
                        label="Auto-refresh"
                    />
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={refreshing ? <CircularProgress size={14} /> : <RefreshIcon />}
                        onClick={handleManualRefresh}
                        disabled={refreshing}
                    >
                        {refreshing ? 'Starting…' : 'Refresh Now'}
                    </Button>
                </Box>
            </Paper>

            {/* Niche list */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Niches ({(data?.niches || []).length})
                </Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
                    Add Niche
                </Button>
            </Box>

            {(data?.niches || []).length === 0 ? (
                <Paper elevation={0} sx={{ ...cardStyle, textAlign: 'center', py: 6 }}>
                    <Typography sx={{ color: 'text.secondary', mb: 2 }}>
                        No niches configured in Firestore yet.
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                        Hardcoded templates are used as fallback. Add niches here to manage them without deploys.
                    </Typography>
                </Paper>
            ) : (
                <Box>
                    {(data?.niches || []).map(({ niche, keyword_count, template_count }) => (
                        <Accordion
                            key={niche}
                            sx={{
                                mb: 1, borderRadius: '10px !important',
                                border: `1px solid ${theme.palette.divider}`,
                                '&:before': { display: 'none' },
                                background: theme.palette.background.paper,
                            }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, mr: 2 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>
                                        {niche.replace(/_/g, ' ')}
                                    </Typography>
                                    <Chip
                                        label={`${keyword_count} keywords`}
                                        size="small"
                                        sx={{ height: 20, fontSize: '0.62rem', bgcolor: alpha('#6366f1', 0.08), color: '#6366f1' }}
                                    />
                                    <Chip
                                        label={`${template_count} templates`}
                                        size="small"
                                        sx={{ height: 20, fontSize: '0.62rem', bgcolor: alpha('#10b981', 0.08), color: '#10b981' }}
                                    />
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0, borderTop: `1px solid ${theme.palette.divider}` }}>
                                <NicheEditor
                                    niche={niche}
                                    onSaved={() => { load(); setSuccessMsg(`"${niche}" saved`); }}
                                    onDelete={handleDeleteNiche}
                                />
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            )}

            <AddNicheDialog
                open={addOpen}
                onClose={() => setAddOpen(false)}
                onAdded={() => { load(); setSuccessMsg('Niche added'); }}
            />
        </Box>
    );
}
